/**
 * The one live announcer (§6.12): one polite and one assertive `aria-live`
 * region, created once at the end of <body>.
 *
 * Use it for result counts after filtering (debounced 500 ms), page changes
 * ("Showing 26–50 of 1,284"), live-feed summaries, mutation outcomes and shell
 * banners. Toasts announce through it too (src/lib/toast.ts).
 */

export type Politeness = 'polite' | 'assertive';

const regions: Partial<Record<Politeness, HTMLElement>> = {};
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const announcedOnce = new Set<string>();

function region(politeness: Politeness): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const existing = regions[politeness];
  if (existing?.isConnected) return existing;
  const el = document.createElement('div');
  el.setAttribute('aria-live', politeness);
  el.setAttribute('aria-atomic', 'true');
  el.setAttribute('role', politeness === 'assertive' ? 'alert' : 'status');
  el.dataset.announcer = politeness;
  // Visually hidden, still read (the sr-only pattern without depending on CSS).
  Object.assign(el.style, {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0',
  });
  document.body.append(el);
  regions[politeness] = el;
  return el;
}

/** Creates both regions early, so the first message is not lost while a region mounts. */
export function mountAnnouncer(): void {
  region('polite');
  region('assertive');
}

/**
 * Speaks a message. Clearing first and setting on the next frame makes screen
 * readers repeat an identical message.
 */
export function announce(
  message: string,
  politeness: Politeness = 'polite',
): void {
  const el = region(politeness);
  if (!el || !message) return;
  el.textContent = '';
  const set = () => {
    el.textContent = message;
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(set);
  else setTimeout(set, 0);
}

/**
 * Speaks only the last message for `key` after `delayMs` of quiet. Result
 * counts while typing a filter use this (500 ms).
 */
export function announceDebounced(
  key: string,
  message: string,
  delayMs = 500,
  politeness: Politeness = 'polite',
): void {
  const pending = timers.get(key);
  if (pending) clearTimeout(pending);
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key);
      announce(message, politeness);
    }, delayMs),
  );
}

/** Speaks a message once per `id` for the life of the page (shell banners). */
export function announceOnce(
  id: string,
  message: string,
  politeness: Politeness = 'assertive',
): void {
  if (announcedOnce.has(id)) return;
  announcedOnce.add(id);
  announce(message, politeness);
}

/** Lets a banner be announced again after it cleared and came back. */
export function forgetAnnouncement(id: string): void {
  announcedOnce.delete(id);
}
